import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, MessageCircle, Package } from 'lucide-react';
import { produceAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import SupportThread from '@/components/support/SupportThread';
import { fmtDate, fmtQty, unitLabel } from '@/components/produce/OfferCard';

/** Chats about farm produce: offtakers with farmers and cluster managers. */
// "Buyer", "Farmer", or "Manager of …" when the farmer runs a cluster.
function whoLabel(t, chat) {
  if (chat.other_clusters) return t('produceMarket.chats.managerOf', { names: chat.other_clusters });
  return t(`produceMarket.chats.${chat.other_type}`, chat.other_type);
}

export function ProduceChatsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [chats, setChats] = useState(null);

  useEffect(() => {
    produceAPI.chats().then((res) => setChats(res.data.chats)).catch(() => setChats([]));
  }, []);

  return (
    <div className="p-4 md:p-6 animate-fadeIn pb-8">
      <h1 className="font-heading text-2xl font-bold">{t('produceMarket.chats.title')}</h1>
      <p className="text-muted-foreground mb-4">{t('produceMarket.chats.subtitle')}</p>
      {chats === null && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></div>}
      {chats && chats.length === 0 && <p className="text-center text-gray-600 py-8">{t('produceMarket.chats.none')}</p>}
      <ul className="divide-y rounded-2xl border bg-white" data-testid="produce-chat-list">
        {(chats || []).map((c) => (
          <li key={c.id}>
            <button type="button" className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    onClick={() => navigate(`/produce/chats/${c.id}`)} data-testid={`produce-chat-${c.id}`}>
              <span className="w-10 h-10 rounded-full bg-farm-primary/10 grid place-items-center shrink-0">
                <MessageCircle className="w-5 h-5 text-farm-primary" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold truncate">{c.other_name}</span>
                <span className="block text-xs text-gray-500">{whoLabel(t, c)}</span>
                <span className="block text-sm text-gray-600 truncate">
                  {c.last_sender_is_me ? t('produceMarket.chats.you') : ''}{c.last_preview}
                </span>
              </span>
              <span className="text-right shrink-0">
                <span className="block text-xs text-gray-500">{fmtDate(c.last_message_at, i18n.language)}</span>
                {c.unread > 0 && <span className="inline-block mt-1 rounded-full bg-red-600 text-white text-xs px-2">{c.unread}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One chat. Text only; the first message carries the offer asked about. */
export function ProduceChatPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [chat, setChat] = useState(null);

  const load = useCallback(async (after) => {
    const res = await produceAPI.messages(chatId, after);
    setChat(res.data.chat);
    return res.data;
  }, [chatId]);
  const send = useCallback(async (draft) => (await produceAPI.send(chatId, draft.body)).data, [chatId]);
  const markRead = useCallback(async () => { await produceAPI.read(chatId).catch(() => {}); }, [chatId]);

  const offerCard = (m) => m.offer && (
    <div className={`mb-1 rounded-xl px-2 py-1.5 text-xs ${m.mine ? 'bg-white/15' : 'bg-farm-primary/10'}`} data-testid="chat-offer">
      <p className="font-semibold flex items-center gap-1"><Package className="w-3.5 h-3.5" />{m.offer.item}
        {m.offer.cluster_name ? ` · ${m.offer.cluster_name}` : ''}</p>
      <p>{t('produceMarket.market.available', { qty: fmtQty(m.offer.quantity, i18n.language), unit: unitLabel(t, m.offer.unit) })}
        {m.offer.best_before ? ` · ${t('produceMarket.market.bestBefore', { date: fmtDate(m.offer.best_before, i18n.language) })}` : ''}</p>
    </div>
  );

  return (
    <div className="flex flex-col pb-28" data-testid="produce-chat-page">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-3 py-2 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/produce/chats')} aria-label={t('chat.back')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="min-w-0">
          <p className="font-semibold truncate">{chat?.other_name || ''}</p>
          <p className="text-xs text-muted-foreground">{chat ? whoLabel(t, chat) : ''}</p>
        </div>
      </div>
      <SupportThread
        threadKey={chatId}
        load={load}
        send={send}
        markRead={markRead}
        allowPhotos={false}
        renderExtra={offerCard}
        emptyText={t('produceMarket.chats.none')}
      />
    </div>
  );
}
